import { createContext, ReactNode, FC, useState, useEffect } from "react";

import { FeatureFlagService } from "@/api/services/interfaces/FeatureFlag";


/**
 * TODO: We need to consider having an enumeration for the feature flag strings
 * for the front end and back end when we start adding more flags
 */
export interface FeatureFlagContextWrapperProps {
    /**
     * The service providing the feature flags
     */
    service: FeatureFlagService;

    /**
     * The child elements passed to the component
     */
    children: ReactNode | ReactNode[];
}

export interface FeatureFlags {
    /**
     * Checks if a singular string is in the feature flags
     */
    hasFeatureFlag: (flag: string) => boolean;

    /**
     * Checks if a list of strings are all in the feature flags
     */
    hasFeatureFlags: (flags: string[]) => boolean;

    /**
     * Returns true once flags have been fetched, so behavior can be different
     * while loading.
     */
    hasFetchedFlags: () => boolean;
}

/**
 * Default implementation of the FeatureFlags
 * Marks all features as "off"
 */
const defaultFeatureFlags: FeatureFlags = {
    hasFetchedFlags: () => false,
    hasFeatureFlag: () => false,
    hasFeatureFlags: () => false,
};

/**
 * Context for interacting with feature flags
 */
export const FeatureFlagContext = createContext<FeatureFlags>(defaultFeatureFlags);

/**
 * React component to provide the feature flag context to child components
 */
export const FeatureFlagContextWrapper: FC<FeatureFlagContextWrapperProps> = ({ service, children }) => {
    const [flags, setFlags] = useState<string[]>();

    useEffect(() => {
        const getFlags = async () => {
            const response = await service.getFeatureFlags();

            setFlags(response);
        };

        getFlags();
    }, [service]);

    const hasFetchedFlags = () => flags !== undefined;

    const hasFeatureFlag = (required_flag: string) => flags?.includes(required_flag) ?? false;

    const hasFeatureFlags = (required_flags: string[]) => required_flags.every((f) => flags?.includes(f));

    const featureFlags: FeatureFlags = {
        hasFetchedFlags,
        hasFeatureFlag,
        hasFeatureFlags,
    };

    return (
        <FeatureFlagContext.Provider value={featureFlags}>{children}</FeatureFlagContext.Provider>
    );
};
